import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { Search, Sparkles, ArrowRight, Settings, Cable } from 'lucide-react';
import { useEndpoints } from '@/api/hooks';
import { tagSlug, endpointSlug } from '@/lib/slug';
import { MethodBadge } from './MethodBadge';
import { cn } from '@/lib/cn';

/**
 * Cmd+K command palette — fuzzy search across endpoints + quick AI prompts.
 *
 * Opens via Cmd/Ctrl+K from anywhere. We use cmdk (the same library shadcn
 * wraps) for the search primitive and Radix Dialog for the modal layer.
 *
 * Categories:
 *   - Endpoints (313): jump to detail page
 *   - AI prompts: prefill Ask-AI bar with a curated question
 *   - Pages: navigate to MCP setup, settings, etc
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const endpoints = useEndpoints();
  const navigate = useNavigate();

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Limit to top 50 endpoints by relevance — cmdk filters client-side already
  // but Command renders all items; capping prevents jank on a 313-item list.
  const visibleEndpoints = useMemo(() => {
    if (!query) return endpoints.slice(0, 30);
    return endpoints.slice(0, 80);
  }, [endpoints, query]);

  const goto = (cb: () => void) => {
    setOpen(false);
    setQuery('');
    setTimeout(cb, 50);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/4 z-50 -translate-x-1/2 w-full max-w-xl',
            'rounded-lg border border-border bg-elevated shadow-2xl overflow-hidden',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
          )}
        >
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search endpoints, run AI prompts, navigate.
          </Dialog.Description>
          <Command label="Command Menu" shouldFilter={true}>
            <div className="flex items-center gap-2 px-3 h-12 border-b border-border">
              <Search size={14} className="text-fg-muted shrink-0" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Search endpoints, ask AI, navigate..."
                className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-muted focus:outline-none"
              />
              <kbd className="font-mono text-2xs text-fg-muted shrink-0">ESC</kbd>
            </div>

            <Command.List className="max-h-96 overflow-y-auto py-2">
              <Command.Empty className="px-4 py-8 text-center text-sm text-fg-muted">
                No results.
              </Command.Empty>

              <Command.Group heading="AI prompts">
                <PaletteItem
                  icon={<Sparkles size={13} />}
                  label="Explain currently selected endpoint"
                  hint="⌘J"
                  onSelect={() =>
                    goto(() => {
                      // Focus the AskAiBar — handled by global shortcut
                      window.dispatchEvent(new CustomEvent('playground:focus-ai', { detail: 'Explain this endpoint in plain language' }));
                    })
                  }
                />
                <PaletteItem
                  icon={<Sparkles size={13} />}
                  label="Generate Python example"
                  onSelect={() =>
                    goto(() =>
                      window.dispatchEvent(
                        new CustomEvent('playground:focus-ai', {
                          detail: 'Generate a Python example using requests, with proper auth.',
                        })
                      )
                    )
                  }
                />
                <PaletteItem
                  icon={<Sparkles size={13} />}
                  label="Debug my last response"
                  onSelect={() =>
                    goto(() =>
                      window.dispatchEvent(
                        new CustomEvent('playground:focus-ai', {
                          detail: 'What does the last response error mean and how do I fix it?',
                        })
                      )
                    )
                  }
                />
              </Command.Group>

              <Command.Group heading="Navigate">
                <PaletteItem
                  icon={<Cable size={13} />}
                  label="MCP setup"
                  hint="/mcp"
                  onSelect={() => goto(() => navigate({ to: '/mcp' }))}
                />
                <PaletteItem
                  icon={<ArrowRight size={13} />}
                  label="Welcome / home"
                  hint="/"
                  onSelect={() => goto(() => navigate({ to: '/' }))}
                />
                <PaletteItem
                  icon={<Settings size={13} />}
                  label="Settings"
                  hint="(coming soon)"
                  onSelect={() => goto(() => {})}
                />
              </Command.Group>

              <Command.Group heading={`Endpoints (${endpoints.length})`}>
                {visibleEndpoints.map((ep) => (
                  <Command.Item
                    key={ep.operationId}
                    value={`${ep.method} ${ep.path} ${ep.summary} ${ep.tag}`}
                    onSelect={() =>
                      goto(() =>
                        navigate({
                          to: '/endpoints/$tag/$operation',
                          params: {
                            tag: tagSlug(ep.tag),
                            operation: endpointSlug(ep.operationId),
                          },
                        })
                      )
                    }
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer data-[selected=true]:bg-overlay/60 transition-colors"
                  >
                    <MethodBadge method={ep.method} />
                    <span className="font-mono text-2xs truncate flex-1 text-fg">
                      {ep.path}
                    </span>
                    <span className="text-2xs text-fg-muted shrink-0 truncate max-w-[180px]">
                      {ep.tag}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteItem({
  icon,
  label,
  hint,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={label}
      onSelect={onSelect}
      className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer data-[selected=true]:bg-overlay/60 text-fg-secondary transition-colors"
    >
      <span className="text-accent">{icon}</span>
      <span className="flex-1">{label}</span>
      {hint && <span className="text-2xs text-fg-muted font-mono">{hint}</span>}
    </Command.Item>
  );
}
